import { MutationTree } from 'vuex';
import { WorkflowState, Workflow } from './types';
import { AppTemplate } from '../app_template/types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// エラーをクリアする
const resetErrors = (state: WorkflowState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;
    createState.isCreated = false;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorCode = undefined;
    csvState.errorMsg = undefined;
};

export const mutations: MutationTree<WorkflowState> = {
    // 検知ルール検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; workflows: Array<Workflow> }) {
        state.workflowCount = payload.count;
        state.workflows = payload.workflows;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // ワークフロー作成系
    [types.CLEAR_WORKFLOW_ERROR](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // ワークフロー詳細情報参照系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.error = false;
        showState.errorCode = undefined;
        showState.errorMsg = undefined;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: { workflow: Workflow; templates: Array<AppTemplate> }) {
        const showState = state.show;
        state.workflowDetails.workflow = payload.workflow;
        state.workflowDetails.templates = payload.templates.length > 0 ? payload.templates : [];
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // ワークフロー更新系
    [types.UPDATE_START](state) {
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // ワークフロー削除系
    [types.DESTROY_START](state) {
        resetErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { code: string; message: string }) {
        const csvState = state.csv;
        csvState.errorCode = payload.code;
        csvState.errorMsg = payload.message;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const updateState = state.update;
        updateState.isProcessing = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;

        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    // ワークフロー詳細リセット
    [types.DETAIL_RESET](state) {
        state.workflowDetails.workflow = undefined;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.workflows = undefined;
        state.workflowDetails.workflow = undefined;
        state.workflowDetails.templates = undefined;
        state.workflowCount = 0;
    },
};
